import { AxiosInstance } from 'axios';
import config from 'config/Config';
import { initAxios } from 'utils/axios';
import {
  UpdateBudgetSettingsRequest,
  UpdateLineItemActualsRequest,
} from '../../utils/constants/budget';
import {
  BudgetStatusChangeRequest,
  CreateBudgetRequest,
  CreateFileUploadRequest,
  CreateLineItemRequest,
  DuplicateBudgetRequest,
  UpdateBudgetAmountRequest,
  UpdateFixedBrokenLineItemsRequest,
} from '../../utils/customTypes';

class BudgetListAPI {
  instance: AxiosInstance;

  constructor() {
    this.instance = initAxios(config.get('backendURL'));
  }

  fetchBudgetList = async () => {
    const { data } = await this.instance.get('v2/budgets');
    return data;
  };

  fetchLineItemsAmountsByBudgetId = async (budgetId: string) => {
    const { data } = await this.instance.get(
      `v2/budgets/${budgetId}/lineItemAmounts`
    );
    return data;
  };

  fetchAttachmentsByLineItemId = async (lineItemId: string) => {
    const { data } = await this.instance.get(
      `v2/budgets/lineItems/${lineItemId}/files`
    );
    return data;
  };

  createNewBudget = async (newBudgetData: CreateBudgetRequest) => {
    try {
      const { data } = await this.instance.post('v2/budgets', {
        ...newBudgetData,
      });
      return data;
    } catch (error) {
      throw error;
    }
  };

  duplicateBudget = async (newBudgetData: DuplicateBudgetRequest) => {
    const { data } = await this.instance.post(`v2/budgets/duplicate`, {
      ...newBudgetData,
    });
    return data;
  };

  renameBudget = async (id: string, title: string) => {
    const { data } = await this.instance.post(`v2/budgets/${id}/rename`, {
      title,
    });
    return data;
  };

  changeBudgetStatus = async (
    statusChangeRequest: BudgetStatusChangeRequest
  ) => {
    const { data } = await this.instance.post('v2/budgets/changeStatus', {
      ...statusChangeRequest,
    });
    return data;
  };

  // updateLineItemNote = async (id: string, note: string) => {
  //   const { data } = await this.instance.post('v2/budgets/lineItems/note', {
  //     id: id,
  //     note: note,
  //   });
  //   return data;
  // };

  updateLineItemNote = async (id: string, note: string) => {
    try {
      const { data } = await this.instance.post('v2/budgets/lineItems/note', {
        id: id,
        note: note,
      });
      return data;
    } catch (error: any) {
      throw new Error(
        'No response received from the server. Please try again later.'
      );
    }
  };

  deleteBudget = async (budgetId: string) => {
    try {
      const { data } = await this.instance.delete(`v2/budgets/${budgetId}`);
      return data;
    } catch (error: any) {
      throw new Error(
        'No response received from the server. Please try again later.'
      );
    }
  };

  updateLineItem = async (lineItemRequest: CreateLineItemRequest) => {
    const { data } = await this.instance.post('v2/budgets/lineItems/update', {
      ...lineItemRequest,
    });
    return data;
  };

  updateLineItemActual = async (
    lineItemRequest: UpdateLineItemActualsRequest
  ) => {
    const { data } = await this.instance.post(
      'v2/budgets/lineItems/updateActuals',
      {
        ...lineItemRequest,
      }
    );
    return data;
  };

  updateLineItemActualForProject = async (
    lineItemRequest: UpdateLineItemActualsRequest
  ) => {
    const { data } = await this.instance.post(
      'v2/budgets/lineItems/updateProjectActuals',
      {
        ...lineItemRequest,
      }
    );
    return data;
  };

  deleteLineItem = async (lineItemId: string) => {
    try {
      const { data } = await this.instance.delete(
        `v2/budgets/lineItems/${lineItemId}`
      );
      return data;
    } catch (error) {
      throw error;
    }
  };

  saveBudgetFileUploadInfo = async (
    fileUploadRequest: CreateFileUploadRequest[]
  ) => {
    const { data } = await this.instance.post('v2/budgets/lineItems/files', [
      ...fileUploadRequest,
    ]);
    return data;
  };

  deleteBudgetFile = async (id: string) => {
    const { data } = await this.instance.delete(
      `v2/budgets/lineItems/file/${id}`
    );
    return data;
  };

  fetchBudgetAmountsByBudgetId = async (budgetId: string) => {
    const { data } = await this.instance.get(
      `v2/budgets/budgetAmount/${budgetId}`
    );
    return data;
  };

  updateBudgetAmount = async (
    updateBudgetAmountRequest: UpdateBudgetAmountRequest
  ) => {
    const { data } = await this.instance.put('v2/budgets/budgetAmount', {
      ...updateBudgetAmountRequest,
    });
    return data;
  };

  getProjectLineItemInfo = async (projectId: string) => {
    const { data } = await this.instance.get(
      'v2/budgets/projectActiveBudgets/lineItemAmounts/' + projectId
    );
    return data;
  };

  updateFixedBrokenLineItems = async (
    updateFixedBrokenLineItemsRequest: UpdateFixedBrokenLineItemsRequest[]
  ) => {
    const { data } = await this.instance.put(
      'v2/budgets/brokenLineItemsUpdate',
      [...updateFixedBrokenLineItemsRequest]
    );

    return data;
  };

  fetchBudgetSettings = async () => {
    const { data } = await this.instance.get('v2/budgets/settings');
    return data;
  };

  updateBudgetSettings = async (
    updateBudgetSettingsRequest: UpdateBudgetSettingsRequest
  ) => {
    const { data } = await this.instance.post('v2/budgets/settings', {
      ...updateBudgetSettingsRequest,
    });
    return data;
  };

  updateCostOfLabor = async (lineItemRequest: CreateLineItemRequest) => {
    const { data } = await this.instance.post(
      'v2/budgets/lineItems/updateCostOfLabor',
      {
        ...lineItemRequest,
      }
    );
    return data;
  };

  fetchCostOfLaborActuals = async (budgetId: string) => {
    const { data } = await this.instance.get(
      `v2/budgets/${budgetId}/projectsActualLaborCost`
    );
    return data;
  };
}

const budgetListAPI = new BudgetListAPI();

export default budgetListAPI;
